import { EditorElement } from '@wix/component-protocol/schema';

export interface SlotBuilderInfo {
  slotKeyName: string;
  innerElementPath?: string[];
  slotId: string;
}

type SlotPath = {
  slotKeyName: string;
  innerElementPath?: string[];
};

export class ManifestSlotPathFinder {
  private manifest: EditorElement;

  constructor(manifest: EditorElement) {
    this.manifest = manifest;
  }

  /**
   * Find a specific slot by its ID and return its path information
   */
  findSlotInManifest(slotId: string): {
    slotKeyName: string;
    innerElementPath?: string[];
  } {
    const result = this.searchSlotInManifest(this.manifest, slotId);

    if (!result) {
      throw new Error(`Slot with ID '${slotId}' not found in manifest`);
    }

    return result;
  }

  /**
   * Find all slots in the manifest and return their information
   */
  findAllSlotsInManifest(): SlotBuilderInfo[] {
    const slots: SlotBuilderInfo[] = [];
    this.collectAllSlots(this.manifest, [], slots);
    return slots;
  }

  private searchSlotInManifest(
    manifest: EditorElement,
    targetSlotId: string,
    currentPath: string[] = [],
    searchCurrentData = true,
  ): SlotPath | null {
    if (searchCurrentData) {
      const rootDataSlotResult = this.findSlotInDataNamespace(
        (manifest as any).data,
        currentPath,
        targetSlotId,
      );
      if (rootDataSlotResult) {
        return rootDataSlotResult;
      }
    }

    if (!manifest.elements) {
      return null;
    }

    for (const [elementKey, element] of Object.entries(manifest.elements)) {
      const elementPath = [...currentPath, elementKey];

      // Try to find slot in inline element
      const inlineSlotResult = this.findSlotInInlineElement(
        element,
        elementPath,
        targetSlotId,
      );
      if (inlineSlotResult) {
        return inlineSlotResult;
      }

      // Try to find slot in ref element
      const refSlotResult = this.findSlotInRefElement(
        element,
        elementPath,
        targetSlotId,
      );
      if (refSlotResult) {
        return refSlotResult;
      }

      // Try to find slot in nested elements
      const nestedSlotResult = this.findSlotInNestedElements(
        element,
        elementPath,
        targetSlotId,
      );
      if (nestedSlotResult) {
        return nestedSlotResult;
      }
    }

    return null;
  }

  private findSlotInInlineElement(
    element: any,
    elementPath: string[],
    targetSlotId: string,
  ): SlotPath | null {
    if (
      element.elementType !== 'inlineElement' ||
      !element.inlineElement?.data
    ) {
      return null;
    }

    return this.findSlotInDataNamespace(
      element.inlineElement.data,
      elementPath,
      targetSlotId,
    );
  }

  private findSlotInDataNamespace(
    data: any,
    elementPath: string[],
    targetSlotId: string,
  ): SlotPath | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    for (const [dataKey, dataValue] of Object.entries(data)) {
      if (
        this.isSlotContainer(dataValue) &&
        (dataValue as any).container.slot.slotId === targetSlotId
      ) {
        const slotPath: SlotPath = {
          slotKeyName: dataKey,
        };
        if (elementPath.length > 0) {
          slotPath.innerElementPath = elementPath;
        }
        return slotPath;
      }
    }

    return null;
  }

  private findSlotInRefElement(
    element: any,
    elementPath: string[],
    targetSlotId: string,
  ): SlotPath | null {
    if (
      element.elementType !== 'refElement' ||
      !element.refElement?.resolvedElement
    ) {
      return null;
    }

    return this.searchSlotInManifest(
      element.refElement.resolvedElement as EditorElement,
      targetSlotId,
      elementPath,
    );
  }

  private findSlotInNestedElements(
    element: any,
    elementPath: string[],
    targetSlotId: string,
  ): SlotPath | null {
    if (!element.inlineElement?.elements) {
      return null;
    }

    return this.searchSlotInManifest(
      element.inlineElement as EditorElement,
      targetSlotId,
      elementPath,
      false,
    );
  }

  private collectAllSlots(
    manifest: EditorElement,
    currentPath: string[],
    slots: SlotBuilderInfo[],
    collectCurrentData = true,
  ): void {
    if (collectCurrentData) {
      this.collectSlotsFromDataNamespace(
        (manifest as any).data,
        currentPath,
        slots,
      );
    }

    if (!manifest.elements) {
      return;
    }

    for (const [elementKey, element] of Object.entries(manifest.elements)) {
      const elementPath = [...currentPath, elementKey];

      // Collect slots from inline element
      this.collectSlotsFromInlineElement(element, elementPath, slots);

      // Collect slots from ref element
      this.collectSlotsFromRefElement(element, elementPath, slots);

      // Collect slots from nested elements
      this.collectSlotsFromNestedElements(element, elementPath, slots);
    }
  }

  private collectSlotsFromInlineElement(
    element: any,
    elementPath: string[],
    slots: SlotBuilderInfo[],
  ): void {
    if (
      element.elementType !== 'inlineElement' ||
      !element.inlineElement?.data
    ) {
      return;
    }

    this.collectSlotsFromDataNamespace(
      element.inlineElement.data,
      elementPath,
      slots,
    );
  }

  private collectSlotsFromDataNamespace(
    data: any,
    elementPath: string[],
    slots: SlotBuilderInfo[],
  ): void {
    if (!data || typeof data !== 'object') {
      return;
    }

    for (const [dataKey, dataValue] of Object.entries(data)) {
      if (this.isSlotContainer(dataValue)) {
        const slot: SlotBuilderInfo = {
          slotKeyName: dataKey,
          slotId: (dataValue as any).container.slot.slotId,
        };
        if (elementPath.length > 0) {
          slot.innerElementPath = elementPath;
        }
        slots.push(slot);
      }
    }
  }

  private collectSlotsFromRefElement(
    element: any,
    elementPath: string[],
    slots: SlotBuilderInfo[],
  ): void {
    if (
      element.elementType !== 'refElement' ||
      !element.refElement?.resolvedElement
    ) {
      return;
    }

    this.collectAllSlots(
      element.refElement.resolvedElement as EditorElement,
      elementPath,
      slots,
    );
  }

  private collectSlotsFromNestedElements(
    element: any,
    elementPath: string[],
    slots: SlotBuilderInfo[],
  ): void {
    if (!element.inlineElement?.elements) {
      return;
    }

    this.collectAllSlots(
      element.inlineElement as EditorElement,
      elementPath,
      slots,
      false,
    );
  }

  private isSlotContainer(dataValue: any): dataValue is {
    dataType: 'container';
    container: {
      containerType: 'slot';
      slot: { slotId: string };
    };
  } {
    return (
      dataValue &&
      typeof dataValue === 'object' &&
      'dataType' in dataValue &&
      dataValue.dataType === 'container' &&
      'container' in dataValue &&
      dataValue.container &&
      typeof dataValue.container === 'object' &&
      'containerType' in dataValue.container &&
      dataValue.container.containerType === 'slot' &&
      'slot' in dataValue.container &&
      dataValue.container.slot &&
      typeof dataValue.container.slot === 'object' &&
      'slotId' in dataValue.container.slot &&
      typeof dataValue.container.slot.slotId === 'string'
    );
  }
}
